<template>
  <div class="team-dashboard">
    <MainNavbar />

    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <div v-if="!loading && currentTeam.team_id" class="vb-dashboard-preview" role="status">
      <i class="fa-solid fa-circle-info" aria-hidden="true" />
      <span>
        Match scheduling and challenges for volleyball are coming soon. Roster and team profile
        below are loaded from your account.
      </span>
    </div>

    <!-- Main Content -->
    <main class="dashboard-main">
      <!-- Dashboard Header with Team Info and Tabs -->
      <TeamHeader
        :team="currentTeam"
        :user-role="userRole"
        :active-tab="activeTab"
        :tabs="tabs"
        @image-error="handleImageError"
        @edit-team="editTeam"
        @invite-players="invitePlayers"
        @delete-team="confirmDeleteTeam"
        @tab-change="activeTab = $event"
      />

      <!-- Dashboard Content -->
      <div class="dashboard-content">
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" class="tab-content">
          <!-- Stats Cards -->
          <StatsCards
            :upcoming-matches-count="upcomingMatches.length"
            :upcoming-match-rate="upcomingMatchRate"
            :win-rate="winRate"
            :win-trend="winTrend"
            :win-trend-icon="winTrendIcon"
            :win-trend-text="winTrendText"
            :active-players-count="activePlayersCount"
            :new-players-this-month="newPlayersThisMonth"
            :team-rating="teamRating"
            :rating-count="ratingCount"
          />

          <!-- volleyball-specific team profile (from registration) -->
          <div v-if="currentTeam.team_id" class="card vb-team-profile">
            <div class="card-header">
              <div class="card-title">
                <i class="fa-solid fa-volleyball"></i>
                <h2>Team Profile</h2>
              </div>
            </div>
            <div class="card-body">
              <div class="vb-profile-grid">
                <div class="vb-profile-field">
                  <div class="vb-profile-label">
                    <i class="fa-solid fa-people-group"></i>
                    Game Format
                  </div>
                  <div class="vb-profile-value">{{ currentTeam.game_format || 'â€”' }}</div>
                </div>
                <div class="vb-profile-field">
                  <div class="vb-profile-label">
                    <i class="fa-solid fa-users"></i>
                    Roster Target
                  </div>
                  <div class="vb-profile-value">{{ currentTeam.roster_target || 'â€”' }}</div>
                </div>
                <div class="vb-profile-field">
                  <div class="vb-profile-label">
                    <i class="fa-solid fa-building"></i>
                    Court Type
                  </div>
                  <div class="vb-profile-value">{{ courtLabel(currentTeam.court_type) || '—' }}</div>
                </div>
                <div class="vb-profile-field">
                  <div class="vb-profile-label">
                    <i class="fa-solid fa-clock"></i>
                    Match Format
                  </div>
                  <div class="vb-profile-value">{{ matchFormatLabel(currentTeam.match_format) || '—' }}</div>
                </div>
                <div class="vb-profile-field vb-profile-field--wide">
                  <div class="vb-profile-label">
                    <i class="fa-solid fa-location-dot"></i>
                    Home Court
                  </div>
                  <div class="vb-profile-value">{{ currentTeam.home_ground || 'â€”' }}</div>
                </div>
                <div class="vb-profile-field">
                  <div class="vb-profile-label">
                    <i class="fa-solid fa-map"></i>
                    Location
                  </div>
                  <div class="vb-profile-value">
                    {{ [currentTeam.county, currentTeam.constituency].filter(Boolean).join(', ') || 'â€”' }}
                  </div>
                </div>
              </div>
              <p v-if="currentTeam.bio" class="vb-team-bio">{{ currentTeam.bio }}</p>
            </div>
          </div>

          <!-- Challenges Section -->
          <ChallengesSection
            :challenges="challenges"
            :loading="loadingChallenges"
            :processing-challenge="processingChallenge"
            :format-date="formatDate"
            :format-time="formatTime"
            @refresh="refreshChallenges"
            @accept="acceptChallenge"
            @reject="rejectChallenge"
            @view-details="viewChallengeDetails"
            @image-error="handleImageError"
          />

          <!-- Two Column Layout -->
          <div class="content-grid">
            <!-- Left Column -->
            <div class="column">
              <UpcomingMatches
                :matches="upcomingMatches"
                :team-id="currentTeam.team_id"
                :format-date="formatDate"
                :format-time="formatTime"
                :format-match-status="formatMatchStatus"
                @view-all="viewAllMatches"
                @view-match="viewMatch"
                @view-details="viewMatchDetails"
                @organize-match="organizeMatch"
                @view-tactics="viewTacticsAndFormations"
              />

              <TeamPreferences
                :team="currentTeam"
                :hide-availability="true"
                :format-preferred-days="formatPreferredDays"
                :capitalize-text="capitalizeText"
                @edit="editPreferences"
              />
            </div>

            <!-- Right Column -->
            <div class="column">
              <TeamPlayers
                :players="teamMembers"
                :user-role="userRole"
                :format-date="formatDate"
                @invite="invitePlayers"
                @remove="removePlayer"
              />

              <RecentActivity
                :activities="recentActivity"
                :format-time-ago="formatTimeAgo"
              />
            </div>
          </div>
        </div>

        <!-- Matches Tab -->
        <div v-if="activeTab === 'matches'" class="tab-content">
          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-volleyball"></i>
              <h2>All Matches</h2>
            </div>
            <div class="section-controls">
              <div class="search-box">
                <i class="fa-solid fa-search"></i>
                <input 
                  v-model="matchSearch" 
                  type="text" 
                  placeholder="Search matches..."
                  @input="filterMatches"
                >
                <button v-if="matchSearch" class="clear-search" @click="clearMatchSearch">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              <button class="btn-action edit-btn" @click="organizeMatch">
                <i class="fa-solid fa-plus"></i>
                New Match
              </button>
            </div>
          </div>

          <!-- Filter Tabs -->
          <div class="filter-tabs">
            <button 
              v-for="filter in matchFilters" 
              :key="filter.id"
              class="tab-btn"
              :class="{ 'active': matchFilter === filter.id }"
              @click="matchFilter = filter.id"
            >
              <i :class="filter.icon"></i>
              <span>{{ filter.label }}</span>
              <span v-if="filter.count" class="tab-count">{{ filter.count }}</span>
            </button>
          </div>

          <!-- Matches Grid -->
          <div class="matches-grid booking-list-container">
            <transition-group name="booking-list">
              <div 
                v-for="match in filteredMatches" 
                :key="match.id"
                class="match-card booking-card"
                :class="match.status"
                @click="viewMatch(match.id)"
              >
                <div class="booking-header">
                  <div class="booking-id">
                    <i class="fa-solid fa-volleyball"></i>
                    <span>Match #{{ match.id }}</span>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-date">
                      <i class="fa-solid fa-clock"></i>
                      {{ formatDate(match.date) }}
                    </span>
                    <span class="booking-status" :class="match.status">
                      {{ formatMatchStatus(match.status) }}
                    </span>
                  </div>
                </div>
                <div class="booking-body">
                  <div class="match-teams">
                    <div class="team-info home-team">
                      <div class="team-name">{{ currentTeam.name }}</div>
                      <div class="team-score">{{ match.home_score || '-' }}</div>
                    </div>
                    <div class="vs-divider">VS</div>
                    <div class="team-info away-team">
                      <div class="team-name">{{ match.opponent_name || 'TBD' }}</div>
                      <div class="team-score">{{ match.away_score || '-' }}</div>
                    </div>
                  </div>
                  <div class="match-details">
                    <div class="venue-info">
                      <i class="fa-solid fa-map-marker-alt"></i>
                      <span>{{ match.venue?.name || 'Venue TBD' }}</span>
                    </div>
                    <div class="time-info">
                      <i class="fa-solid fa-clock"></i>
                      <span>{{ formatTime(match.date) }}</span>
                    </div>
                  </div>
                </div>
                <div class="booking-actions">
                  <button class="btn-action btn-secondary" @click.stop="viewMatchDetails(match.id)">
                    <i class="fa-solid fa-eye"></i>
                    Details
                  </button>
                  <button 
                    v-if="['confirmed', 'scheduled'].includes((match.status || '').toLowerCase())" 
                    class="btn-action btn-primary" 
                    @click.stop="viewTacticsAndFormations(match.id)"
                  >
                    <i class="fa-solid fa-chess-board"></i>
                    Tactics and Formations
                  </button>
                  <button 
                    v-if="isPastMatch(match) && isChallengerForMatch(match)" 
                    class="btn-action btn-primary" 
                    @click.stop="updateMatchStats(match.id)"
                  >
                    <i class="fa-solid fa-chart-line"></i>
                    Update Match Stats
                  </button>
                </div>
              </div>
            </transition-group>

            <!-- Empty State -->
            <div v-if="filteredMatches.length === 0" class="empty-state">
              <div class="empty-icon">
                <i class="fa-solid fa-volleyball"></i>
              </div>
              <h3>No Matches Found</h3>
              <p>{{ matchSearch ? 'Try a different search' : 'volleyball match scheduling is not available yet.' }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredMatches.length > 0" class="pagination">
            <button class="pagination-btn" :disabled="matchPage === 1" @click="matchPage--">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <div class="page-numbers">
              <button 
                v-for="page in totalMatchPages" 
                :key="page"
                class="page-btn"
                :class="{ active: matchPage === page }"
                @click="matchPage = page"
              >
                {{ page }}
              </button>
            </div>
            <button class="pagination-btn" :disabled="matchPage === totalMatchPages" @click="matchPage++">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- Players Tab -->
        <div v-if="activeTab === 'players'" class="tab-content">
          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-users"></i>
              <h2>Team Players</h2>
            </div>
            <div class="section-controls">
              <div class="search-box">
                <i class="fa-solid fa-search"></i>
                <input 
                  v-model="playerSearch" 
                  type="text" 
                  placeholder="Search players..."
                  @input="filterPlayers"
                >
                <button v-if="playerSearch" class="clear-search" @click="clearPlayerSearch">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              <button v-if="isTeamOwner" class="btn-action edit-btn" @click="invitePlayers">
                <i class="fa-solid fa-user-plus"></i>
                Invite Players
              </button>
            </div>
          </div>

          <!-- Player Grid -->
          <div class="players-grid booking-list-container">
            <transition-group name="booking-list">
              <div 
                v-for="player in filteredPlayers" 
                :key="player.member_id"
                class="player-card booking-card"
              >
                <div class="booking-header">
                  <div class="booking-customer">
                    <div class="customer-avatar">
                      <i class="fa-solid fa-user"></i>
                    </div>
                    <div class="customer-info">
                      <h4>{{ player.name }}</h4>
                      <div class="customer-contact">
                        <span>{{ player.position || 'Player' }}</span>
                        <span v-if="player.email">
                          <i class="fa-solid fa-envelope"></i>
                          {{ player.email }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-status" :class="player.status">
                      {{ player.status }}
                    </span>
                  </div>
                </div>
                <div class="booking-body">
                  <div class="player-stats-grid">
                    <div class="stat">
                      <strong>{{ player.stats?.matches || 0 }}</strong>
                      <span>Matches</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.stats?.points || 0 }}</strong>
                      <span>Points</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.stats?.rebounds || 0 }}</strong>
                      <span>Rebounds</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.stats?.assists || 0 }}</strong>
                      <span>Assists</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.rating || 'N/A' }}</strong>
                      <span>Rating</span>
                    </div>
                  </div>
                  <div class="player-details">
                    <div class="detail-item">
                      <i class="fa-solid fa-user-tag"></i>
                      <span>{{ player.role }}</span>
                    </div>
                    <div v-if="player.is_captain" class="detail-item">
                      <i class="fa-solid fa-crown"></i>
                      <span>Captain</span>
                    </div>
                    <div class="detail-item">
                      <i class="fa-solid fa-calendar"></i>
                      <span>Joined {{ formatDate(player.joined_at) }}</span>
                    </div>
                  </div>
                </div>
                <div class="booking-actions">
                  <button
                    v-if="userRole === 'owner' && !isOwnerPlayer(player)"
                    class="btn-action btn-danger"
                    @click.stop="removePlayer(player.member_id)"
                  >
                    <i class="fa-solid fa-user-minus"></i>
                    Remove
                  </button>
                </div>
              </div>
            </transition-group>

            <!-- Empty State -->
            <div v-if="filteredPlayers.length === 0" class="empty-state">
              <div class="empty-icon">
                <i class="fa-solid fa-users-slash"></i>
              </div>
              <h3>No Players Found</h3>
              <p>{{ playerSearch ? 'Try a different search' : 'Invite players to build your team' }}</p>
              <button v-if="isTeamOwner" class="btn-refresh" @click="invitePlayers">
                <i class="fa-solid fa-user-plus"></i>
                Invite Players
              </button>
            </div>
          </div>
        </div>

        <!-- Invites Tab -->
        <div v-if="false && activeTab === 'invites'" class="tab-content">
          <InvitePlayersPanel
            v-if="showInvitePanel && isTeamOwner && currentTeam.team_id"
            :team-id="currentTeam.team_id"
            sport="volleyball"
            @close="showInvitePanel = false"
            @invites-sent="onInvitesSent"
          />

          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-envelope"></i>
              <h2>Team Invites</h2>
            </div>
            <div class="section-controls">
              <div class="filter-tabs">
                <button 
                  v-for="filter in inviteFilters" 
                  :key="filter.id"
                  class="tab-btn"
                  :class="{ 'active': inviteStatusFilter === filter.id }"
                  @click="inviteStatusFilter = filter.id"
                >
                  <i :class="filter.icon"></i>
                  <span>{{ filter.label }}</span>
                  <span v-if="filter.count !== undefined" class="tab-count">{{ filter.count }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Invites List -->
          <div class="invites-list booking-list-container">
            <transition-group name="booking-list">
              <div 
                v-for="invite in filteredInvites" 
                :key="invite.invite_id"
                class="invite-card booking-card"
                :class="invite.status"
              >
                <div class="booking-header">
                  <div class="booking-customer">
                    <div class="customer-avatar">
                      <i class="fa-solid fa-user"></i>
                    </div>
                    <div class="customer-info">
                      <h4>{{ invite.receiver_name || invite.receiver_email || 'Unknown' }}</h4>
                      <div class="customer-contact">
                        <span v-if="invite.receiver_email">
                          <i class="fa-solid fa-envelope"></i>
                          {{ invite.receiver_email }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-status" :class="invite.status">
                      {{ capitalizeStatus(invite.status) }}
                    </span>
                  </div>
                </div>
                <div class="booking-body">
                  <div class="booking-details">
                    <div class="time-details">
                      <div class="time-slot">
                        <i class="fa-solid fa-calendar"></i>
                        Sent {{ formatDate(invite.created_at) }}
                      </div>
                      <div class="duration">
                        <i class="fa-solid fa-clock"></i>
                        Expires {{ formatDate(invite.expires_at) }}
                      </div>
                    </div>
                    <div class="booking-amount">
                      <div class="amount-label">Invite Code</div>
                      <div class="amount-value">{{ invite.invite_code }}</div>
                    </div>
                  </div>
                  <!-- Player Stats (if invitee is already a player) -->
                  <div v-if="invite.player_stats" class="booking-notes">
                    <i class="fa-solid fa-chart-bar"></i>
                    <p>
                      <strong>Player Stats:</strong>
                      {{ invite.player_stats.points || 0 }} pts â€¢
                      {{ invite.player_stats.rebounds || 0 }} reb â€¢
                      {{ invite.player_stats.assists || 0 }} ast
                    </p>
                  </div>
                  <div v-else-if="invite.status === 'accepted'" class="booking-notes">
                    <i class="fa-solid fa-check-circle"></i>
                    <p>Invite accepted - Player is now a team member</p>
                  </div>
                  <div v-else-if="invite.status === 'pending'" class="booking-notes">
                    <i class="fa-solid fa-hourglass-half"></i>
                    <p>Waiting for response</p>
                  </div>
                  <div v-else-if="invite.status === 'declined'" class="booking-notes">
                    <i class="fa-solid fa-times-circle"></i>
                    <p>Invite was declined</p>
                  </div>
                  <div v-else-if="invite.status === 'expired'" class="booking-notes">
                    <i class="fa-solid fa-exclamation-triangle"></i>
                    <p>Invite has expired</p>
                  </div>
                </div>
                <div class="booking-actions">
                  <button 
                    v-if="invite.status === 'pending'" 
                    class="btn-action btn-secondary" 
                    @click.stop="cancelInvite(invite.invite_id)"
                  >
                    <i class="fa-solid fa-times"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </transition-group>

            <!-- Empty State -->
            <div v-if="filteredInvites.length === 0" class="empty-state">
              <div class="empty-icon">
                <i class="fa-solid fa-envelope-open"></i>
              </div>
              <h3>No Invites Found</h3>
              <p>{{ inviteStatusFilter !== 'all' ? 'No invites with this status' : 'No invites have been sent yet' }}</p>
            </div>
          </div>
        </div>

        <!-- Stats Tab -->
        <div v-if="activeTab === 'stats'" class="tab-content">
          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-chart-bar"></i>
              <h2>Team Statistics</h2>
            </div>
          </div>

          <!-- Quick Stats -->
          <div class="quick-stats">
            <div class="stat-item">
              <i class="fa-solid fa-trophy"></i>
              <div>
                <span class="stat-value">{{ currentTeam.wins || 0 }}</span>
                <span class="stat-label">Wins</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-times"></i>
              <div>
                <span class="stat-value">{{ currentTeam.losses || 0 }}</span>
                <span class="stat-label">Losses</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-equals"></i>
              <div>
                <span class="stat-value">{{ currentTeam.draws || 0 }}</span>
                <span class="stat-label">Draws</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-volleyball"></i>
              <div>
                <span class="stat-value">{{ teamPoints }}</span>
                <span class="stat-label">Points</span>
              </div>
            </div>
          </div>

          <!-- Performance Overview -->
          <div class="stats-overview">
            <div class="stat-big-card">
              <h3>Performance Overview</h3>
              <div class="stats-row">
                <div class="stat-item">
                  <strong>{{ winRate }}%</strong>
                  <span>Win Rate</span>
                </div>
                <div class="stat-item">
                  <strong>{{ avgPointsPerGame }}</strong>
                  <span>Avg PPG</span>
                </div>
                <div class="stat-item">
                  <strong>{{ avgReboundsPerGame }}</strong>
                  <span>Avg RPG</span>
                </div>
                <div class="stat-item">
                  <strong>{{ longestWinStreak }}</strong>
                  <span>Longest Win Streak</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Player Stats Table -->
          <div class="card">
            <div class="card-header">
              <div class="card-title">
                <i class="fa-solid fa-user-chart"></i>
                <h2>Player Statistics</h2>
              </div>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="stats-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Position</th>
                      <th>Matches</th>
                      <th>Points</th>
                      <th>Rebounds</th>
                      <th>Assists</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="player in teamMembers" :key="player.member_id">
                      <td class="player-cell">
                        <div class="player-avatar-sm">
                          <i class="fa-solid fa-user"></i>
                        </div>
                        <span>{{ player.name }}</span>
                      </td>
                      <td>{{ player.position || '-' }}</td>
                      <td>{{ player.stats?.matches || 0 }}</td>
                      <td>{{ player.stats?.points || 0 }}</td>
                      <td>{{ player.stats?.rebounds || 0 }}</td>
                      <td>{{ player.stats?.assists || 0 }}</td>
                      <td>
                        <span class="rating-badge">
                          {{ player.rating || 'N/A' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <ThirdpartyFooter />

    <!-- Confirmation Modal -->
    <DeleteConfirmModal
      :show="showDeleteConfirm"
      :team="currentTeam"
      @cancel="cancelDelete"
      @confirm="deleteTeam"
    />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import TeamHeader from "@/components/teamdashboard/TeamHeader.vue";
import StatsCards from "@/components/teamdashboard/StatsCards.vue";
import ChallengesSection from "@/components/teamdashboard/ChallengesSection.vue";
import UpcomingMatches from "@/components/teamdashboard/UpcomingMatches.vue";
import TeamPreferences from "@/components/teamdashboard/TeamPreferences.vue";
import TeamPlayers from "@/components/teamdashboard/TeamPlayers.vue";
import RecentActivity from "@/components/teamdashboard/RecentActivity.vue";
import DeleteConfirmModal from "@/components/teamdashboard/DeleteConfirmModal.vue";
import InvitePlayersPanel from "@/components/teamdashboard/InvitePlayersPanel.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { useAuthStore } from "@/authen/auth";
import { buildSignInLocation } from "@/utils/authRedirect";

const OWNER_TEAM_ACTION_DENIED_MESSAGE = "Sorry unable to complete request";

export default {
  name: 'VolleyballTeamDashboard',
  loadingMessage: 'Loading team data...',
  mixins: [toastMixin, globalLoadingMixin],
  components: {
    MainNavbar,
    ToastContainer,
    TeamHeader,
    StatsCards,
    ChallengesSection,
    UpcomingMatches,
    TeamPreferences,
    TeamPlayers,
    RecentActivity,
    DeleteConfirmModal,
    InvitePlayersPanel,
    ThirdpartyFooter
  },
  
  data() {
    return {
      loading: false,
      activeTab: 'overview',
      currentTeam: {
        team_id: null,
        name: '',
        team_type: '',
        bio: '',
        logo_url: null,
        phone: '',
        home_ground: '',
        home_ground_pin_location: '',
        county: '',
        constituency: '',
        skill_level: '',
        availability: '',
        preferences: {},
        is_active: true,
        is_verified: false,
        total_matches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        member_count: 0,
        owner_id: null,
        owner_name: '',
        created_at: null,
        updated_at: null,
        game_format: '',
        roster_target: null,
        court_type: '',
        match_format: '',
        sport: 'volleyball',
      },
      teamMembers: [],
      teamMatches: [],
      upcomingMatches: [],
      challenges: [],
      loadingChallenges: false,
      processingChallenge: null,
      recentActivity: [],
      teamInvites: [],
      inviteStatusFilter: 'all',
      
      tabs: [
        { id: 'overview', label: 'Overview', icon: 'fa-solid fa-home' },
        { id: 'matches', label: 'Matches', icon: 'fa-solid fa-volleyball' },
        { id: 'players', label: 'Players', icon: 'fa-solid fa-users' },
        { id: 'stats', label: 'Stats', icon: 'fa-solid fa-chart-bar' }
      ],
      
      matchSearch: '',
      playerSearch: '',
      matchFilter: 'all',
      
      matchPage: 1,
      matchesPerPage: 6,
      showDeleteConfirm: false,
      showInvitePanel: false,
      userRole: 'owner' // owner, captain, member
    };
  },
  
  computed: {
    isTeamOwner() {
      return this.userRole === "owner";
    },

    activePlayersCount() {
      return this.teamMembers.filter(m => m.status === 'active').length;
    },
    
    winRate() {
      const total = this.currentTeam.wins + this.currentTeam.losses + this.currentTeam.draws;
      return total > 0 ? Math.round((this.currentTeam.wins / total) * 100) : 0;
    },
    
    winTrend() {
      return (this.currentTeam.total_matches || 0) > 0 ? 'positive' : 'neutral';
    },
    
    winTrendIcon() {
      if (this.winTrend === 'positive') return 'fa-solid fa-arrow-up';
      if (this.winTrend === 'negative') return 'fa-solid fa-arrow-down';
      return 'fa-solid fa-minus';
    },
    
    winTrendText() {
      const total = (this.currentTeam.total_matches || 0);
      if (total === 0) return 'No match history yet';
      return `${this.winRate}% win rate`;
    },
    
    upcomingMatchRate() {
      return 0;
    },
    
    newPlayersThisMonth() {
      return this.teamMembers.filter(m => {
        const joinDate = new Date(m.joined_at);
        const now = new Date();
        return joinDate.getMonth() === now.getMonth() && joinDate.getFullYear() === now.getFullYear();
      }).length;
    },
    
    teamRating() {
      const ratings = this.teamMembers
        .filter(m => m.rating)
        .map(m => m.rating);
      return ratings.length > 0 
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
        : 'N/A';
    },
    
    ratingCount() {
      return this.teamMembers.filter(m => m.rating).length;
    },
    
    teamPoints() {
      return this.teamMembers.reduce((total, player) => total + (player.stats?.points || 0), 0);
    },

    avgPointsPerGame() {
      const totalMatches = this.currentTeam.total_matches || 1;
      return (this.teamPoints / totalMatches).toFixed(1);
    },

    avgReboundsPerGame() {
      const total = this.teamMembers.reduce((sum, p) => sum + (p.stats?.rebounds || 0), 0);
      const games = this.currentTeam.total_matches || 1;
      return (total / games).toFixed(1);
    },
    
    longestWinStreak() {
      return 0;
    },
    
    filteredMatches() {
      let matches = this.teamMatches;
      
      // Apply search filter
      if (this.matchSearch) {
        const search = this.matchSearch.toLowerCase();
        matches = matches.filter(match => 
          match.opponent_name?.toLowerCase().includes(search) ||
          match.venue?.name?.toLowerCase().includes(search) ||
          match.status?.toLowerCase().includes(search)
        );
      }
      
      // Apply status filter
      if (this.matchFilter !== 'all') {
        matches = matches.filter((match) => {
          if (this.matchFilter === "upcoming") {
            return !this.isPastMatch(match);
          }
          if (this.matchFilter === "past") {
            return this.isPastMatch(match);
          }
          return (match.status || "").toLowerCase() === this.matchFilter;
        });
      }

      // Apply pagination
      const start = (this.matchPage - 1) * this.matchesPerPage;
      const end = start + this.matchesPerPage;
      return matches.slice(start, end);
    },
    
    totalMatchPages() {
      let matches = this.teamMatches;
      
      if (this.matchSearch) {
        const search = this.matchSearch.toLowerCase();
        matches = matches.filter(match => 
          match.opponent_name?.toLowerCase().includes(search) ||
          match.venue?.name?.toLowerCase().includes(search) ||
          match.status?.toLowerCase().includes(search)
        );
      }
      
      if (this.matchFilter !== "all") {
        matches = matches.filter((match) => {
          if (this.matchFilter === "upcoming") {
            return !this.isPastMatch(match);
          }
          if (this.matchFilter === "past") {
            return this.isPastMatch(match);
          }
          return (match.status || "").toLowerCase() === this.matchFilter;
        });
      }

      return Math.ceil(matches.length / this.matchesPerPage);
    },
    
    filteredPlayers() {
      let players = this.teamMembers;
      
      if (this.playerSearch) {
        const search = this.playerSearch.toLowerCase();
        players = players.filter(player => 
          player.name?.toLowerCase().includes(search) ||
          player.position?.toLowerCase().includes(search) ||
          player.email?.toLowerCase().includes(search)
        );
      }
      
      return players;
    },
    
    // Update filter counts
    matchFilters() {
      return [
        { 
          id: 'all', 
          label: 'All Matches', 
          icon: 'fa-solid fa-list',
          count: this.teamMatches.length
        },
        { 
          id: 'upcoming', 
          label: 'Upcoming', 
          icon: 'fa-solid fa-calendar-plus',
          count: this.teamMatches.filter((m) => !this.isPastMatch(m)).length
        },
        { 
          id: 'past', 
          label: 'Past', 
          icon: 'fa-solid fa-history',
          count: this.teamMatches.filter((m) => this.isPastMatch(m)).length
        },
        { 
          id: 'pending', 
          label: 'Pending', 
          icon: 'fa-solid fa-clock',
          count: this.teamMatches.filter(m => m.status === 'pending').length
        }
      ];
    },
    
    filteredInvites() {
      let invites = this.teamInvites;
      
      if (this.inviteStatusFilter !== 'all') {
        invites = invites.filter(invite => invite.status === this.inviteStatusFilter);
      }
      
      return invites;
    },
    
    inviteFilters() {
      return [
        { 
          id: 'all', 
          label: 'All', 
          icon: 'fa-solid fa-list',
          count: this.teamInvites.length
        },
        { 
          id: 'pending', 
          label: 'Pending', 
          icon: 'fa-solid fa-clock',
          count: this.teamInvites.filter(i => i.status === 'pending').length
        },
        { 
          id: 'accepted', 
          label: 'Accepted', 
          icon: 'fa-solid fa-check-circle',
          count: this.teamInvites.filter(i => i.status === 'accepted').length
        },
        { 
          id: 'declined', 
          label: 'Declined', 
          icon: 'fa-solid fa-times-circle',
          count: this.teamInvites.filter(i => i.status === 'declined').length
        },
        { 
          id: 'expired', 
          label: 'Expired', 
          icon: 'fa-solid fa-exclamation-triangle',
          count: this.teamInvites.filter(i => i.status === 'expired').length
        }
      ];
    }
  },
  
  beforeMount() {
    const auth = useAuthStore();
    auth.checkToken();
    if (!auth.isAuthenticated) {
      this.$router.replace(buildSignInLocation(this.$route.fullPath));
    }
  },

  async mounted() {
    const tab = this.$route.query?.tab;
    if (tab && this.tabs.some((t) => t.id === tab)) {
      this.activeTab = tab;
    }
    if (this.$route.query?.compose === '1') {
      this.activeTab = 'invites';
      this.showInvitePanel = true;
    }
    await this.loadTeamData();
  },
  
  methods: {
  capitalizeWord(word) {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
},

  capitalizeText(text) {
    if (!text || typeof text !== 'string') return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  },

  formatPreferredDays(days) {
    if (!days || !Array.isArray(days) || days.length === 0) {
      return [];
    }
    
    const dayMap = {
      'monday': 'Monday',
      'tuesday': 'Tuesday',
      'wednesday': 'Wednesday',
      'thursday': 'Thursday',
      'friday': 'Friday',
      'saturday': 'Saturday',
      'sunday': 'Sunday',
      'mon': 'Monday',
      'tue': 'Tuesday',
      'wed': 'Wednesday',
      'thu': 'Thursday',
      'fri': 'Friday',
      'sat': 'Saturday',
      'sun': 'Sunday'
    };
    
    return days.map(day => {
      const dayLower = day.toLowerCase().trim();
      return dayMap[dayLower] || this.capitalizeText(day);
    });
  },
    getAuthToken() {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token')
      );
    },

    showOwnerActionDeniedToast() {
      this.showToast("error", "", OWNER_TEAM_ACTION_DENIED_MESSAGE);
    },

    isOwnerPlayer(player) {
      return String(player?.role || "").toLowerCase() === "owner";
    },

    normalizeTeamMembersForDashboard(members) {
      return members.map((m, index) => {
        const memberId = m.team_member_number ?? m.member_id;
        return {
          ...m,
          member_id: memberId,
          name: m.name || m.player_name || `Member ${index + 1}`,
          email: m.email || m.player_email || "",
          phone: m.phone || m.player_phone || "",
          joined_at: m.joined_at || null,
          stats: {
            matches: m.stats?.matches || 0,
            points: m.points ?? m.stats?.points ?? 0,
            rebounds: m.rebounds ?? m.stats?.rebounds ?? 0,
            assists: m.assists ?? m.stats?.assists ?? 0,
          },
        };
      });
    },

    resolveTeamFromPayload(payload) {
      // Backend `/api/teams/my-teams` returns { message, teams: [...] }
      // Backend `/api/teams/{id}` returns a single team object
      if (!payload) return null;

      const routeTeamId =
        this.$route?.params?.team_id ||
        this.$route?.params?.id ||
        this.$route?.query?.team_id ||
        this.$route?.query?.teamId ||
        null;

      if (Array.isArray(payload.teams)) {
        if (payload.teams.length === 0) return null;
        if (routeTeamId) {
          const match = payload.teams.find(
            (t) => String(t?.team_id) === String(routeTeamId)
          );
          return match || payload.teams[0];
        }
        return payload.teams[0];
      }

      if (payload.team && typeof payload.team === 'object') return payload.team;
      if (payload.team_id || payload.team_id_num) return payload;

      return null;
    },

    mapTeamToViewModel(teamData) {
      if (!teamData) return null;
      return {
        team_id: teamData.team_id ?? teamData.team_id_num ?? null,
        name: teamData.name ?? teamData.team_name ?? '',
        team_type: teamData.team_type ?? '',
        bio: teamData.bio || '',
        logo_url: teamData.logo_url || null,
        phone: teamData.phone || '',
        home_ground: teamData.home_ground || '',
        home_ground_pin_location: teamData.home_ground_pin_location || '',
        county: teamData.county || '',
        constituency: teamData.constituency || '',
        skill_level: this.capitalizeWord(teamData.skill_level) || 'beginner',
        availability: teamData.availability || 'weekends',
        preferences: teamData.preferences || {},
        is_active: teamData.is_active ?? true,
        is_verified: teamData.is_verified || false,
        total_matches: teamData.total_matches || 0,
        wins: teamData.wins || 0,
        losses: teamData.losses || 0,
        draws: (teamData.total_matches || 0) - (teamData.wins || 0) - (teamData.losses || 0), // Calculate draws
        member_count: teamData.member_count || 0,
        owner_id: teamData.owner_id || null,
        owner_name: teamData.owner_name || '',
        created_at: teamData.created_at || null,
        updated_at: teamData.updated_at || null,
        game_format: teamData.game_format || '',
        roster_target: teamData.roster_target ?? null,
        court_type: teamData.court_type || '',
        match_format: teamData.match_format || '',
        sport: teamData.sport || 'volleyball',
      };
    },

    formatLabel(value) {
      const map = { "6v6": "6v6 indoor", "5v5": "5v5", beach: "Beach", both: "Multiple formats" };
      return map[value] || value || '';
    },

    courtLabel(value) {
      const map = { indoor: "Indoor", outdoor: "Outdoor", beach: "Beach", both: "Indoor & outdoor" };
      return map[value] || value || '';
    },

    matchFormatLabel(value) {
      const map = {
        best_of_3: "Best of 3 sets",
        best_of_5: "Best of 5 sets",
        rally_25: "Single set to 25",
        recreational: "Recreational",
      };
      return map[value] || value || '';
    },

    clearUnavailableSections() {
      this.teamMatches = [];
      this.upcomingMatches = [];
      this.challenges = [];
      this.recentActivity = [];
      this.teamInvites = [];
      this.loadingChallenges = false;

      const inviteTab = this.tabs.find((t) => t.id === 'invites');
      if (inviteTab) inviteTab.count = 0;
    },

    async loadTeamData() {
      this.loading = true;
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(
          buildApiUrl('/api/teams/volleyball/my-team?include_members=true'),
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.status === 404) {
          this.$router.replace({ name: 'VolleyballTeamRegistration' });
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const teamData = await response.json();
        const mapped = this.mapTeamToViewModel(teamData);
        if (!mapped?.team_id) {
          throw new Error('Team data loaded, but team_id is missing.');
        }

        this.currentTeam = mapped;
        this.userRole = 'owner';
        this.teamMembers = this.normalizeTeamMembersForDashboard(teamData.members || []);
        await this.loadTeamMatches();
        await this.loadChallenges();
        await this.loadTeamInvites();
      } catch (error) {
        console.error('Error loading volleyball team data:', error);
        this.showToast(
          'error',
          'Failed to load team',
          error.message || 'Please try again',
        );
      } finally {
        this.loading = false;
      }
    },

    featureSoon(feature) {
      this.showToast(
        "info",
        feature,
        "This feature is not available for volleyball teams yet.",
      );
    },

    async refreshChallenges() {
      await this.loadChallenges();
      this.showToast('success', 'Refreshed', 'Challenge requests updated');
    },

    async acceptChallenge(challengeId) {
      this.processingChallenge = challengeId;
      try {
        const token = this.getAuthToken();
        if (!token) throw new Error('No authentication token found');
        const response = await fetch(
          buildApiUrl(`/api/matches/volleyball/${challengeId}/accept`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to accept challenge');
        }
        this.showToast('success', 'Challenge Accepted', 'The match has been confirmed.');
        this.challenges = this.challenges.filter((c) => c.id !== challengeId);
        await this.loadTeamMatches();
      } catch (error) {
        this.showToast('error', 'Error', error.message || 'Failed to accept challenge');
      } finally {
        this.processingChallenge = null;
      }
    },

    async rejectChallenge(challengeId) {
      this.processingChallenge = challengeId;
      try {
        const token = this.getAuthToken();
        if (!token) throw new Error('No authentication token found');
        const response = await fetch(
          buildApiUrl(`/api/matches/volleyball/${challengeId}/reject`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to reject challenge');
        }
        this.showToast('success', 'Challenge Rejected', 'The challenge request has been declined.');
        this.challenges = this.challenges.filter((c) => c.id !== challengeId);
      } catch (error) {
        this.showToast('error', 'Error', error.message || 'Failed to reject challenge');
      } finally {
        this.processingChallenge = null;
      }
    },

    async loadTeamMatches() {
      try {
        if (!this.currentTeam?.team_id) return;
        const token = this.getAuthToken();
        if (!token) return;
        const response = await fetch(
          buildApiUrl(`/api/matches/volleyball/teams/${this.currentTeam.team_id}/matches`),
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          this.teamMatches = data.matches || [];
          this.upcomingMatches = this.teamMatches.filter((match) => !this.isPastMatch(match));
        } else {
          this.teamMatches = [];
          this.upcomingMatches = [];
        }
      } catch (error) {
        this.teamMatches = [];
        this.upcomingMatches = [];
      }
    },

    async loadChallenges() {
      this.loadingChallenges = true;
      try {
        if (!this.currentTeam?.team_id) return;
        const token = this.getAuthToken();
        if (!token) throw new Error('No authentication token found');
        const response = await fetch(
          buildApiUrl(`/api/matches/volleyball/teams/${this.currentTeam.team_id}/challenges`),
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          this.challenges = data.challenges || [];
        } else {
          this.challenges = [];
        }
      } catch (error) {
        this.challenges = [];
      } finally {
        this.loadingChallenges = false;
      }
    },

    viewChallengeDetails() {
      this.featureSoon("Challenge details");
    },

    capitalizeStatus(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    
    formatMatchStatus(status) {
      if (!status) return 'Unknown';
      const statusMap = {
        'pending': 'Awaiting opponent to confirm',
        'confirmed': 'Confirmed',
        'scheduled': 'Scheduled',
        'past': 'Past',
        'completed': 'Completed',
        'cancelled': 'Cancelled',
        'postponed': 'Postponed'
      };
      return statusMap[status.toLowerCase()] || this.capitalizeStatus(status);
    },
    
    cancelInvite() {
      // TODO: Implement cancel invite API call
      this.showToast('info', 'Cancel Invite', 'This feature will be implemented soon');
    },
    
    
    determineUserRole(ownerData) {
      // Get current user email from token or localStorage
      const userEmail = localStorage.getItem('user_email') || '';
      
      // Check if user is owner
      if (ownerData && ownerData.email === userEmail) {
        this.userRole = 'owner';
        return;
      }
      
      // Check if user is captain
      const captain = this.teamMembers.find(m => m.is_captain);
      if (captain && captain.email === userEmail) {
        this.userRole = 'captain';
        return;
      }
      
      // Check if user is member
      const member = this.teamMembers.find(m => m.email === userEmail);
      if (member) {
        this.userRole = 'member';
        return;
      }
      
      // Default to owner for demo purposes
      this.userRole = 'owner';
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    },
    
    formatTimeAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) {
        return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      } else {
        return this.formatDate(dateString);
      }
    },
    
    handleImageError(event) {
      if (!event || !event.target) return;
      
      // Hide the broken image
      event.target.style.display = 'none';
      
      // Find and show the placeholder - check for different placeholder classes
      const parent = event.target.parentElement;
      if (!parent) return;
      
      // Try different placeholder class names
      const placeholder = parent.querySelector('.team-logo-placeholder') || 
                         parent.querySelector('.avatar-placeholder') ||
                         parent.querySelector('.logo-placeholder');
      
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    },
    

    // Navigation methods
    organizeMatch() {
      this.$router.push({ name: "VolleyballOrganizeMatch" }).catch(() => {});
    },

    viewMatch() {
      this.featureSoon("Match details");
    },

    viewMatchDetails() {
      this.featureSoon("Match details");
    },

    matchEndDate(match) {
      if (!match) return null;
      const start = new Date(match.date || match.scheduled_at);
      if (Number.isNaN(start.getTime())) return null;
      const mins = parseInt(match.duration ?? match.duration_minutes ?? 40, 10) || 40;
      return new Date(start.getTime() + mins * 60000);
    },

    isPastMatch(match) {
      if (!match) return false;
      const status = (match.status || "").toLowerCase();
      if (["completed", "cancelled", "past"].includes(status)) return true;
      const end = this.matchEndDate(match);
      if (end && end < new Date()) return true;
      return false;
    },

    isChallengerForMatch(match) {
      if (!match || !this.currentTeam || this.currentTeam.team_id == null) return false;
      const homeTeamId = match.home_team_id ?? match.team_id;
      return Number(homeTeamId) === Number(this.currentTeam.team_id);
    },

    updateMatchStats() {
      this.featureSoon("Update match stats");
    },

    viewTacticsAndFormations() {
      this.featureSoon("Lineups");
    },

    async removePlayer(memberId) {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      const player = this.teamMembers.find((m) => m.member_id === memberId);
      if (!player) return;
      if (String(player.role || "").toLowerCase() === "owner") {
        this.showToast("error", "Not allowed", "You cannot remove the team owner.");
        return;
      }
      if (!confirm(`Remove ${player.name} from the team?`)) return;
      this.featureSoon("Remove player");
    },

    invitePlayers() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      this.featureSoon("Invite players");
    },

    async loadTeamInvites() {
      try {
        if (!this.currentTeam?.team_id) return;
        const token = this.getAuthToken();
        if (!token) return;

        const response = await fetch(
          buildApiUrl(`/api/teams/volleyball/${this.currentTeam.team_id}/invites`),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          this.teamInvites = data.invites || [];
          const inviteTab = this.tabs.find((t) => t.id === "invites");
          if (inviteTab) {
            inviteTab.count = this.teamInvites.filter((i) => i.status === "pending").length;
          }
        } else {
          this.teamInvites = [];
        }
      } catch (error) {
        console.warn("Error loading volleyball invites:", error);
        this.teamInvites = [];
      }
    },

    onInvitesSent() {
      this.showInvitePanel = false;
      this.loadTeamInvites();
    },

    editTeam() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      this.featureSoon("Edit team");
    },
    
    editPreferences() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      this.featureSoon("Edit preferences");
    },
    
    viewAllMatches() {
      this.activeTab = 'matches';
    },
    
    filterMatches() {
      this.matchPage = 1;
    },
    
    clearMatchSearch() {
      this.matchSearch = '';
      this.filterMatches();
    },
    
    filterPlayers() {
      // Player filtering logic
    },
    
    clearPlayerSearch() {
      this.playerSearch = '';
      this.filterPlayers();
    },
    
    confirmDeleteTeam() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      if (!this.currentTeam?.team_id) {
        this.showToast("error", "No team", "There is no team to delete.");
        return;
      }
      this.showDeleteConfirm = true;
    },
    
    cancelDelete() {
      this.showDeleteConfirm = false;
    },
    
    async deleteTeam() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        this.showDeleteConfirm = false;
        return;
      }
      const teamId = this.currentTeam?.team_id;
      if (!teamId) {
        this.showToast("error", "No team", "Missing team.");
        this.showDeleteConfirm = false;
        return;
      }

      const token = this.getAuthToken();
      if (!token) {
        this.showToast("error", "Sign in required", "Please log in again.");
        this.showDeleteConfirm = false;
        this.$router.push({ name: "CustomerSignIn", query: { redirect: this.$route.fullPath } });
        return;
      }

      try {
        const response = await fetch(
          buildApiUrl(`/api/teams/volleyball/${teamId}/delete`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const errorData = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            this.showDeleteConfirm = false;
            return;
          }
          const detail = errorData.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.map((e) => e.msg || e).join("; ")
                : "Failed to delete team";
          throw new Error(msg);
        }

        const teamName = this.currentTeam.name || "Your team";
        this.showDeleteConfirm = false;
        this.showToast(
          "success",
          "Team deleted",
          `${teamName} has been removed. Returning to Match Hub.`
        );
        this.$router.push({ name: "MatchesHub" });
      } catch (error) {
        this.showToast("error", "Deletion failed", error.message || "Failed to delete team");
        this.showDeleteConfirm = false;
      }
    },
  }
};
</script>

<style scoped>
@import "@/routes/match_hub/shared/teamDashboard.css";

.vb-dashboard-preview {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 1400px;
  margin: 0 auto 16px;
  padding: 14px 18px;
  border-radius: 12px;
  background: var(--background-color3);
  border: 1px solid var(--border-color-hover1);
  font-size: 0.9rem;
  line-height: 1.45;
  color: var(--text-color1);
}

.vb-dashboard-preview i {
  color: var(--border-color-hover);
  margin-top: 2px;
}

.vb-team-profile {
  margin-bottom: 24px;
}

.vb-team-profile .card-header {
  padding: 14px 20px;
}

.vb-team-profile .card-title h2 {
  font-size: 1.05rem;
  font-weight: 600;
}

.vb-team-profile .card-title i {
  font-size: 1rem;
}

.vb-team-profile .card-body {
  padding: 16px 20px 20px;
}

.vb-profile-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 14px;
}

.vb-profile-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.vb-profile-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--subtext-color);
  line-height: 1.3;
}

.vb-profile-label i {
  font-size: 0.78rem;
  opacity: 0.85;
  flex-shrink: 0;
}

.vb-profile-value {
  width: 100%;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.45;
  color: var(--heading-color);
  word-break: break-word;
}

.vb-team-bio {
  margin: 14px 0 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: var(--text-color2, var(--subtext-color));
  line-height: 1.55;
  font-size: 0.8125rem;
}

@media (max-width: 1024px) {
  .vb-profile-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .vb-profile-field--wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 768px) {
  .vb-dashboard-preview {
    margin-left: 12px;
    margin-right: 12px;
    padding: 12px 14px;
    font-size: 0.8125rem;
  }

  .vb-team-profile .card-header,
  .vb-team-profile .card-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .content-grid {
    gap: 20px;
  }

  .column {
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .vb-profile-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .vb-profile-field {
    padding: 10px 12px;
  }

  .vb-profile-field--wide {
    grid-column: auto;
  }

  .vb-team-bio {
    font-size: 0.8rem;
    padding: 10px 12px;
  }
}
</style>

